// One attempt-status vocabulary for every admin roster and the student report,
// matching corporate-react-v2's ATTEMPT_STATUS_CHIP (labels and tones).
// Framework-free so it runs under plain node: see attemptStatus.test.js.

const REPORT_DELAY_MS = 15 * 60 * 1000

const ATTEMPT_STATUS = {
  pending: { label: 'Not started', color: '#6b7280' },
  inProgress: { label: 'In progress', color: '#1677ff' },
  reportPreparing: { label: 'Preparing Report', color: '#1677ff' },
  reportDelayed: { label: 'Report Delayed', color: '#d97706' },
  dropped: { label: 'Dropped off', color: '#d97706' },
  completed: { label: 'Completed', color: '#16a34a' },
}

const ATTEMPT_STATUS_TOOLTIP = {
  reportPreparing: 'Assessment submitted. The report usually takes 1–2 minutes to prepare.',
  reportDelayed:
    'Assessment submitted, but the report is taking longer than usual. Check back later; contact support if it does not appear.',
}

// Raw status strings the backends send (admin-node "Dropout", student-node
// "In Progress", DB enums, older "Attempted") → status key. Unknown values
// return null so the caller can show them untouched rather than guess.
const RAW_TO_KEY = {
  pending: 'pending',
  notattempted: 'pending',
  notstarted: 'pending',
  inprogress: 'inProgress',
  dropout: 'dropped',
  droppedoff: 'dropped',
  completed: 'completed',
  attempted: 'completed',
}

const toAttemptStatusKey = raw =>
  RAW_TO_KEY[String(raw ?? '').toLowerCase().replace(/[\s_-]/g, '')] || null

// admin-node's AI Interview outcome (aiInterviewScores) is the truth for that
// type — its flags lag the score row. Settled once it carries a verdict or an
// ending label; "Not Yet Scored" is admin-node's own waiting value.
const isAiInterviewSettled = record => {
  const verdict = record?.aiInterviewScores?.aiRecommendation
  return Boolean(verdict) && verdict !== 'Not Yet Scored'
}

// ponytail: inferred on the client from the roster flags (student-node sends
// `isScoreCalculated` + `submittedAt`, admin-node sends `scoresCalculated`
// with no submit time). admin-node / student-node should send the attempt
// status themselves (as corporate-node does) — a part whose scoring failed
// stays "Preparing Report" here, and admin-node rows, lacking a submit time,
// can never turn "Report Delayed".
const resolveAttemptStatus = (record, now = Date.now()) => {
  const key = toAttemptStatusKey(record?.status)
  if (key !== 'completed') {
    return key
  }
  const scored = record.isScoreCalculated ?? record.scoresCalculated
  if (scored !== false || isAiInterviewSettled(record)) {
    return key
  }
  const submittedAt = Date.parse(record.submittedAt)
  return Number.isFinite(submittedAt) && now - submittedAt > REPORT_DELAY_MS
    ? 'reportDelayed'
    : 'reportPreparing'
}

const isReportPreparingStatus = key => key === 'reportPreparing' || key === 'reportDelayed'

module.exports = {
  ATTEMPT_STATUS,
  ATTEMPT_STATUS_TOOLTIP,
  REPORT_DELAY_MS,
  toAttemptStatusKey,
  resolveAttemptStatus,
  isReportPreparingStatus,
}
